import { useState, useEffect, useCallback } from "react";
import {
  mockPartners,
  getCachedPartners,
  setCachedPartners,
} from "@/mocks/adminData";
import type { Partner } from "@/mocks/adminData";
import { useImageUpload } from "@/hooks/useImageUpload";
import { apiGet, apiPost, DEPT_OPTIONS, getDeptLabel } from "@/pages/admin/utils";

export default function PartnersTab({ token }: { token: string }) {
  const [list, setList] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Partner | null>(null);
  const [form, setForm] = useState<Omit<Partner, "id">>({
    name: "", handle: "", tg_url: "", avatar_url: "", dept: "promotion", sort: 0, status: 1,
  });
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error">("success");

  const { trigger: triggerAvatar, el: avatarInput } = useImageUpload(
    (url) => { setForm(prev => ({ ...prev, avatar_url: url })); },
    { uploadPath: "admin/partners/upload", token, fieldName: "avatar" }
  );

  const showMsg = (text: string, type: "success" | "error" = "success") => {
    setMsg(text); setMsgType(type);
    setTimeout(() => setMsg(""), 3000);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet("admin/partners/list", token);
      if (data.code === 200) {
        const raw = data.data;
        let result: Partner[] = [];
        if (Array.isArray(raw)) result = raw;
        else if (raw && Array.isArray(raw.list)) result = raw.list;
        setList(result);
        setCachedPartners(result);
      } else {
        const cached = getCachedPartners();
        setList(cached ?? mockPartners);
      }
    } catch {
      const cached = getCachedPartners();
      setList(cached ?? mockPartners);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "", handle: "", tg_url: "", avatar_url: "", dept: "promotion", sort: 0, status: 1 });
    setShowForm(true);
  };

  const openEdit = (p: Partner) => {
    setEditing(p);
    setForm({ name: p.name, handle: p.handle, tg_url: p.tg_url, avatar_url: p.avatar_url, dept: p.dept, sort: p.sort, status: p.status });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) { showMsg("请填写名称", "error"); return; }
    try {
      const path = editing ? "admin/partners/edit" : "admin/partners/add";
      const body = editing ? { ...form, id: editing.id } : form;
      const data = await apiPost(path, token, body as Record<string, unknown>);
      if (data.code === 200) {
        showMsg(editing ? "修改成功" : "添加成功");
        setShowForm(false);
        fetchList();
      } else {
        let newList: Partner[];
        if (editing) {
          newList = list.map(p => p.id === editing.id ? { ...editing, ...form } : p);
        } else {
          newList = [...list, { id: Date.now(), ...form }];
        }
        setList(newList);
        setCachedPartners(newList);
        setShowForm(false);
        showMsg((editing ? "修改成功" : "添加成功") + "（已保存到本地缓存）");
      }
    } catch {
      let newList: Partner[];
      if (editing) {
        newList = list.map(p => p.id === editing.id ? { ...editing, ...form } : p);
      } else {
        newList = [...list, { id: Date.now(), ...form }];
      }
      setList(newList);
      setCachedPartners(newList);
      setShowForm(false);
      showMsg((editing ? "修改成功" : "添加成功") + "（已保存到本地缓存）");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("确认删除？")) return;
    try {
      const data = await apiPost("admin/partners/delete", token, { id });
      if (data.code === 200) {
        showMsg("已删除");
        fetchList();
      } else {
        const newList = list.filter(p => p.id !== id);
        setList(newList);
        setCachedPartners(newList);
        showMsg("已删除（已同步本地缓存）");
      }
    } catch {
      const newList = list.filter(p => p.id !== id);
      setList(newList);
      setCachedPartners(newList);
      showMsg("已删除（已同步本地缓存）");
    }
  };

  return (
    <div>
      {avatarInput}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-gray-800">商务人员管理</h2>
        <button onClick={openAdd} className="flex items-center gap-1 bg-red-500 text-white text-xs px-3 py-2 rounded-lg cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap">
          <i className="ri-add-line"></i>添加人员
        </button>
      </div>

      {msg && (
        <div className={`mb-3 p-2 border rounded-lg text-xs ${msgType === "success" ? "bg-green-50 border-green-200 text-green-600" : "bg-red-50 border-red-200 text-red-600"}`}>
          {msg}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">
          <i className="ri-loader-4-line animate-spin text-xl mb-2 block"></i>加载中...
        </div>
      ) : (
        <div className="space-y-2">
          {list.map(p => (
            <div key={p.id} className="flex items-center justify-between bg-white border border-gray-100 rounded-xl p-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-400 to-orange-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                  {p.avatar_url
                    ? <img src={p.avatar_url} className="w-9 h-9 rounded-full object-cover" alt={p.name} />
                    : <span>{p.name.charAt(0)}</span>}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800">{p.name}</p>
                  <p className="text-xs font-medium text-gray-500 truncate">{p.handle} · {getDeptLabel(p.dept)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 1 ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
                  {p.status === 1 ? "启用" : "禁用"}
                </span>
                <button onClick={() => openEdit(p)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                  <i className="ri-edit-line text-gray-500 text-sm"></i>
                </button>
                <button onClick={() => handleDelete(p.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer">
                  <i className="ri-delete-bin-line text-red-400 text-sm"></i>
                </button>
              </div>
            </div>
          ))}
          {list.length === 0 && <div className="text-center py-8 text-gray-400 text-sm">暂无人员</div>}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">{editing ? "编辑人员" : "添加人员"}</h3>
              <button onClick={() => setShowForm(false)} className="w-7 h-7 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">头像</label>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-400 to-orange-400 flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0">
                    {form.avatar_url
                      ? <img src={form.avatar_url} className="w-14 h-14 rounded-full object-cover" alt="头像" />
                      : <span className="text-lg">{form.name ? form.name.charAt(0) : "?"}</span>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button type="button" onClick={triggerAvatar}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs text-gray-600 cursor-pointer transition-colors whitespace-nowrap">
                      <i className="ri-upload-2-line"></i>上传图片
                    </button>
                    {form.avatar_url && (
                      <button type="button" onClick={() => setForm(prev => ({ ...prev, avatar_url: "" }))}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-400 hover:text-red-600 cursor-pointer whitespace-nowrap">
                        <i className="ri-delete-bin-line"></i>移除头像
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs font-medium text-gray-500 mt-1.5">支持 JPG、PNG、GIF 等格式</p>
              </div>

              {([
                { label: "名称", key: "name", placeholder: "如：迪猫" },
                { label: "TG账号", key: "handle", placeholder: "如：@hx_zs888（同时作为认证ID）" },
                { label: "TG链接", key: "tg_url", placeholder: "如：https://t.me/hx_zs888" },
              ] as { label: string; key: keyof typeof form; placeholder: string }[]).map(f => (
                <div key={f.key}>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">{f.label}</label>
                  <input value={form[f.key] as string}
                    onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400"
                    placeholder={f.placeholder} />
                </div>
              ))}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">部门</label>
                  <select value={form.dept} onChange={e => setForm(prev => ({ ...prev, dept: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400">
                    {DEPT_OPTIONS.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">状态</label>
                  <select value={form.status} onChange={e => setForm(prev => ({ ...prev, status: Number(e.target.value) }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-400">
                    <option value={1}>启用</option>
                    <option value={0}>禁用</option>
                  </select>
                </div>
              </div>

              <div className="flex items-start gap-2 bg-orange-50 border border-orange-100 rounded-lg p-3">
                <i className="ri-information-line text-orange-400 text-sm mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-orange-600 leading-relaxed">
                  TG账号将自动加入担保认证白名单，启用状态下可通过担保验证查询。
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={handleSave} className="flex-1 py-2.5 bg-gradient-to-r from-red-500 to-orange-400 text-white rounded-xl text-sm font-semibold cursor-pointer hover:opacity-90 whitespace-nowrap">保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
